"use client";

import { ArrowRight, FileText, Laptop, Rocket, Smartphone, Sparkles, Tablet, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SiemensParts() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const smallCards = [{
    icon: TrendingUp,
    title: "Performance Tuning",
    description: "Advanced axis optimization and drive tuning to maximize machine throughput and accuracy.",
    gradient: "from-chart-2 to-chart-3",
    orbColor: "var(--chart-2)"
  }, {
    icon: Smartphone,
    title: "Remote Diagnostics",
    description: "Real-time monitoring and troubleshooting of Siemens PLC systems from any location.",
    gradient: "from-chart-3 to-primary",
    orbColor: "var(--chart-3)"
  }, {
    icon: Rocket,
    title: "Rapid Deployment",
    description: "Fast-track retrofitting services to minimize downtime and restore production cycles.",
    gradient: "from-chart-4 to-chart-2",
    orbColor: "var(--chart-4)"
  }];
  return <section data-section-id="183" id="siemens-parts" className="relative overflow-hidden bg-background py-20 sm:py-28 lg:py-32 text-foreground">
      {}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-md opacity-10 blur-3xl" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="inline-flex items-center gap-2 rounded-md border border-border bg-card/50 px-4 py-1.5 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5" style={{
            color: "var(--chart-3)"
          }} aria-hidden="true" />
            <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">Technical Excellence</span>
          </div>
          <h2 className="font-default mt-6 text-4xl font-bold tracking-tighter text-foreground sm:text-5xl lg:text-6xl">
            Precision in every{" "}
            <span className="bg-clip-text text-transparent" style={{
            backgroundImage: "linear-gradient(135deg, var(--chart-1) 0%, var(--chart-3) 50%, var(--chart-4) 100%)"
          }}>
              component
            </span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground font-default">IMCNC Designs engineers industrial solutions with mathematical accuracy. Our Siemens-certified expertise ensures your machinery operates at peak efficiency.</p>
        </div>

        {}
        <div ref={gridRef} className={`mt-16 grid grid-cols-1 gap-5 transition-all duration-1000 sm:mt-20 md:grid-cols-2 lg:grid-cols-3 lg:gap-6 ${gridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {}
          <div data-index={0} className="group relative col-span-1 overflow-hidden rounded-md border border-border bg-card p-8 transition-all duration-500 hover:border-chart-3 md:col-span-2" style={{
          boxShadow: "var(--shadow-md)"
        }}>
            {}
            <div className="pointer-events-none absolute inset-0 rounded-md opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{
            background: "linear-gradient(135deg, var(--chart-1), var(--chart-3), var(--chart-4))",
            padding: "1px",
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude"
          }} aria-hidden="true" />

            {}
            <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-md opacity-10 blur-2xl transition-all duration-500 group-hover:opacity-20" style={{
            background: "radial-gradient(circle, var(--chart-3) 0%, transparent 70%)"
          }} aria-hidden="true" />

            <div className="relative">
              <div className="flex h-14 w-14 items-center justify-center rounded-md" style={{
              background: "linear-gradient(135deg, hsl(183 100% 32% / 0.1), hsl(220 48% 12% / 0.05))",
              border: "1px solid hsl(183 100% 32% / 0.2)"
            }}>
                <Sparkles className="h-6 w-6" style={{
                color: "var(--chart-3)"
              }} aria-hidden="true" />
              </div>

              <h3 className="font-default mt-6 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Siemens Retrofitting Logic
              </h3>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground font-default">
                Upgrade legacy hardware to modern Sinumerik standards. We provide 
                end-to-end integration including PLC programming, drive configuration, 
                and custom HMI development for industrial CNC systems.
              </p>

              <Button asChild variant="link" className="mt-5 h-auto p-0 text-base font-semibold uppercase tracking-wider" style={{
              color: "var(--chart-3)"
            }}>
                <Link to="/PartsCalataloguePage" className="group/link inline-flex items-center gap-1.5">View Parts Catalogue<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" aria-hidden="true" data-ast-id="f:/src/pages/Solutions/sections/SiemensParts.tsx|sig:3pyk7a" data-ast-start="5936" data-ast-end="6035"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
              </Button>
            </div>

            {}
            <div className="relative mt-10 -mb-12 sm:-mb-16">
              <div className="relative mx-auto h-48 max-w-2xl overflow-hidden rounded-t-md border border-border bg-background/80 backdrop-blur-sm sm:h-56" style={{
              boxShadow: "var(--shadow-lg)"
            }}>
                {}
                <div className="flex items-center gap-1.5 border-b border-border px-4 py-3">
                  <div className="h-2.5 w-2.5 rounded-md bg-chart-3/30" />
                  <div className="h-2.5 w-2.5 rounded-md bg-chart-3/30" />
                  <div className="h-2.5 w-2.5 rounded-md bg-chart-3/30" />
                </div>

                {}
                <div className="grid grid-cols-12 gap-3 p-5">
                  <div className="col-span-3 space-y-2.5">
                    <div className="h-3 rounded-md bg-muted" />
                    <div className="h-3 w-3/4 rounded-md bg-muted" />
                    <div className="h-3 w-1/2 rounded-md bg-muted" />
                    <div className="mt-4 h-3 rounded-md bg-muted" />
                    <div className="h-3 w-2/3 rounded-md bg-muted" />
                  </div>
                  <div className="col-span-9 space-y-3">
                    <div className="h-16 rounded-md" style={{
                    background: "linear-gradient(135deg, hsl(183 100% 32% / 0.15), hsl(220 48% 12% / 0.1))"
                  }} />
                    <div className="grid grid-cols-3 gap-3">
                      <div className="h-14 rounded-md bg-muted" />
                      <div className="h-14 rounded-md bg-muted" />
                      <div className="h-14 rounded-md bg-muted" />
                    </div>
                    <div className="h-3 w-5/6 rounded-md bg-muted" />
                    <div className="h-3 w-4/6 rounded-md bg-muted" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          {smallCards.map((card, i) => {
          const Icon = card.icon;
          return <div key={card.title} data-index={i + 1} className="group relative overflow-hidden rounded-md border border-border bg-card p-8 transition-all duration-500 hover:border-chart-3" style={{
            boxShadow: "var(--shadow-sm)"
          }}>
                {}
                <div className={`pointer-events-none absolute inset-0 rounded-md bg-gradient-to-br ${card.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-10`} style={{
              padding: "1px",
              WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude"
            }} aria-hidden="true" />

                {}
                <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-md opacity-5 blur-2xl transition-all duration-500 group-hover:opacity-10" style={{
              background: `radial-gradient(circle, ${card.orbColor} 0%, transparent 70%)`
            }} aria-hidden="true" />

                <div className="relative">
                  {}
                  <div className={`flex h-14 w-14 items-center justify-center rounded-md bg-gradient-to-br ${card.gradient}`} style={{
                boxShadow: `0 8px 24px -8px ${card.orbColor}`
              }}>
                    <Icon className="h-6 w-6 text-white" strokeWidth={1.5} aria-hidden="true" />
                  </div>

                  <h3 className="font-default mt-6 text-xl font-bold tracking-tight text-foreground">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground font-default">
                    {card.description}
                  </p>

                  {}
                  <div className="mt-6 h-16 overflow-hidden">
                    {i === 0 && <div className="space-y-2">
                        <div className="h-2 w-full rounded-md" style={{
                    background: "linear-gradient(90deg, var(--chart-3), transparent)"
                  }} />
                        <div className="h-2 w-4/5 rounded-md" style={{
                    background: "linear-gradient(90deg, var(--chart-1), transparent)"
                  }} />
                        <div className="h-2 w-3/5 rounded-md" style={{
                    background: "linear-gradient(90deg, var(--chart-3), transparent)"
                  }} />
                      </div>}
                    {i === 1 && <div className="relative h-full">
                        <svg viewBox="0 0 120 60" className="h-full w-full" preserveAspectRatio="none" aria-hidden="true">
                          <defs>
                            <linearGradient id="seoGrad" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="var(--chart-3)" />
                              <stop offset="100%" stopColor="var(--chart-4)" />
                            </linearGradient>
                          </defs>
                          <polyline points="0,50 20,42 40,38 60,28 80,22 100,12 120,5" fill="none" stroke="url(#seoGrad)" strokeWidth="2" strokeLinecap="square" />
                          <polyline points="0,50 20,42 40,38 60,28 80,22 100,12 120,5 120,60 0,60" fill="url(#seoGrad)" opacity="0.1" />
                        </svg>
                      </div>}
                    {i === 2 && <div className="flex items-end gap-2">
                        <div className="flex h-12 w-16 items-center justify-center rounded-md" style={{
                    background: "linear-gradient(135deg, hsl(183 100% 32% / 0.1), hsl(220 48% 12% / 0.05))",
                    border: "1px solid hsl(183 100% 32% / 0.2)"
                  }}>
                          <Laptop className="h-5 w-5" style={{
                      color: "var(--chart-3)"
                    }} />
                        </div>
                        <div className="flex h-10 w-9 items-center justify-center rounded-md" style={{
                    background: "linear-gradient(135deg, hsl(220 48% 12% / 0.1), hsl(183 100% 32% / 0.05))",
                    border: "1px solid hsl(220 48% 12% / 0.2)"
                  }}>
                          <Tablet className="h-4 w-4" style={{
                      color: "var(--chart-1)"
                    }} />
                        </div>
                        <div className="flex h-8 w-5 items-center justify-center rounded-md" style={{
                    background: "linear-gradient(135deg, hsl(183 100% 32% / 0.1), hsl(220 48% 12% / 0.05))",
                    border: "1px solid hsl(183 100% 32% / 0.2)"
                  }}>
                          <Smartphone className="h-3 w-3" style={{
                      color: "var(--chart-4)"
                    }} />
                        </div>
                      </div>}
                    {i === 3 && <div className="relative flex h-full items-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-md" style={{
                    background: "linear-gradient(135deg, var(--chart-3), var(--chart-1))",
                    boxShadow: "0 8px 20px -6px var(--chart-3)"
                  }}>
                          <Rocket className="h-6 w-6 text-white" strokeWidth={1.5} />
                        </div>
                        <div className="ml-3 h-1 flex-1 rounded-md" style={{
                    background: "linear-gradient(90deg, var(--chart-3), transparent)"
                  }} />
                      </div>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}